import { Mail, Phone, MapPin, Printer, Linkedin, Twitter } from 'lucide-react';
import { Logo } from './Logo';

// Map every link to a section ID, mailto, or null (renders as plain text)
const footerLinks = {
  Product: [
    { label: 'Features', href: '#features' },
    { label: 'Solution', href: '#solution' },
    { label: 'Pricing', href: '#pricing' },
    { label: 'Security', href: '#security' },
    { label: 'Request Demo', href: '#demo' },
  ],
  Company: [
    { label: 'About', href: '/about' },
    { label: 'Blog', href: '/blog' },
    { label: 'Careers', href: '/careers' },
    { label: 'Contact', href: '/contact' },
  ],
  Resources: [
    { label: 'Documentation', href: '/documentation' },
    { label: 'Help Center', href: '/help-center' },
    { label: 'Case Studies', href: '/case-studies' },
  ],
  Legal: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms-of-service' },
    { label: 'Cookie Policy', href: '/cookie-policy' },
  ],
};

const socialLinks = [
  { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/d3command' },
  { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/d3command' },
];

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleLinkClick = (e, href) => {
    if (!href) {
      e.preventDefault();
      return;
    }
    if (href.startsWith('#')) {
      e.preventDefault();
      // If we're on a subpage, navigate to landing page first
      if (window.location.pathname !== '/') {
        window.location.href = '/' + href;
        return;
      }
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (href.startsWith('/')) {
      e.preventDefault();
      window.history.pushState({}, '', href);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo(0, 0);
      return;
    }
    // mailto: and https: links work natively
  };

  return (
    <>
      {/* Gradient divider */}
      <div className="h-px bg-gradient-to-r from-transparent via-indigo-500/20 to-transparent" />

      <footer className="relative bg-[#0A0A0B]" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-12">
            {/* Logo & Contact */}
            <div className="lg:col-span-2 space-y-6">
              <Logo size="large" />
              <p className="text-zinc-500 text-sm leading-relaxed max-w-sm">
                AI-powered operational governance platform transforming conversations into
                execution, analytics, and compliance.
              </p>
              <div className="inline-block py-2 px-4 rounded-lg bg-white/[0.02] border border-white/5">
                <p className="text-xs text-zinc-500">
                  <span className="font-semibold text-zinc-400">D3 Command</span>
                  {' '}&mdash; A product of{' '}
                  <span className="text-zinc-400">D3 Global Holdings LLC</span>
                </p>
              </div>
              <div className="space-y-4 pt-2">
                <a
                  href="mailto:support@d3command.com"
                  className="flex items-center gap-3 text-sm text-zinc-500 hover:text-indigo-400 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-indigo-500/10 transition-colors">
                    <Mail className="w-4 h-4" />
                  </div>
                  support@d3command.com
                </a>
                <a
                  href="tel:+18305803004"
                  className="flex items-center gap-3 text-sm text-zinc-500 hover:text-indigo-400 transition-colors group"
                >
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-indigo-500/10 transition-colors">
                    <Phone className="w-4 h-4" />
                  </div>
                  (830) 580-3004
                </a>
                <div className="flex items-center gap-3 text-sm text-zinc-500">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                    <Printer className="w-4 h-4" />
                  </div>
                  Fax: (830) 532-0417
                </div>
                <div className="flex items-start gap-3 text-sm text-zinc-500">
                  <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <span className="leading-relaxed">
                    2807 Allen St #2164<br />
                    Dallas, TX 75204
                  </span>
                </div>
              </div>
            </div>

            {/* Links */}
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h4 className="font-outfit font-medium text-zinc-300 mb-5 text-sm">
                  {category}
                </h4>
                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.label}>
                      {link.href ? (
                        <a
                          href={link.href}
                          onClick={(e) => handleLinkClick(e, link.href)}
                          className="text-sm text-zinc-600 hover:text-indigo-400 transition-colors duration-200"
                          {...(link.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                          data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {link.label}
                        </a>
                      ) : (
                        <span className="text-sm text-zinc-700 cursor-default">
                          {link.label}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Bottom Bar */}
          <div className="mt-16 pt-8 border-t border-white/5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-sm text-zinc-600 text-center md:text-left space-y-1">
                <p className="font-medium text-zinc-500">
                  &copy; {currentYear} D3 Command. All rights reserved.
                </p>
                <p className="text-zinc-600">
                  D3 Command is a product of D3 Global Holdings LLC.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    className="w-10 h-10 rounded-xl bg-white/5 hover:bg-indigo-500/10 hover:border-indigo-500/30 border border-transparent flex items-center justify-center text-zinc-500 hover:text-indigo-400 transition-all duration-300 group"
                    aria-label={social.name}
                  >
                    <social.icon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};
